/**
 * Enhanced Add-ons functionality with independent buy buttons
 * Each add-on can be added to cart independently without page reload
 */

class AddOnsManager {
  constructor() {
    this.addOnsSection = null;
    this.init();
  }

  init() {
    this.addOnsSection = document.querySelector('[data-add-ons-section]');
    
    if (!this.addOnsSection) {
      console.log('Add-ons section not found');
      return;
    }

    console.log('Add-ons manager initialized with independent buy buttons');
    this.setupEventListeners();
  }

  setupEventListeners() {
    // Listen for buy button clicks on add-ons
    const buyButtons = this.addOnsSection.querySelectorAll('[data-add-on-buy-button]');
    console.log(`Found ${buyButtons.length} add-on buy buttons`);
    
    buyButtons.forEach(button => {
      console.log('Attaching listener to button:', button);
      button.addEventListener('click', (e) => {
        console.log('Button clicked:', e.target);
        this.handleAddOnBuyClick(e.target.closest('[data-add-on-buy-button]'));
      });
    });
  }

  async handleAddOnBuyClick(button) {
    if (button.disabled || button.classList.contains('adding')) {
      return;
    }

    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    const addOnItem = button.closest('[data-add-on-item]');

    console.log('Adding add-on to cart:', { variantId, productId });

    // Show loading state
    button.classList.add('adding');
    const textSpan = button.querySelector('.add-on-buy-btn-text');
    const loadingSpan = button.querySelector('.add-on-buy-btn-loading');
    
    if (textSpan) textSpan.style.visibility = 'hidden';
    if (loadingSpan) loadingSpan.style.display = 'flex';

    try {
      // Add single item to cart
      await this.addItemToCart(variantId);
      
      // Reset button to normal state
      button.classList.remove('adding');
      button.classList.add('added');
      if (textSpan) textSpan.style.visibility = 'visible';
      if (loadingSpan) loadingSpan.style.display = 'none';
      
      // Remove success state after brief moment
      setTimeout(() => {
        button.classList.remove('added');
      }, 1000);
      
    } catch (error) {
      // Show error state
      button.classList.remove('adding');
      if (textSpan) {
        const originalText = textSpan.textContent;
        textSpan.textContent = 'Error';
        textSpan.style.visibility = 'visible';
      }
      if (loadingSpan) loadingSpan.style.display = 'none';
      
      // Reset button after 2 seconds
      setTimeout(() => {
        if (textSpan) textSpan.textContent = 'Add to Cart';
      }, 2000);
    }
  }

  async addItemToCart(variantId) {
    const item = {
      id: variantId,
      quantity: 1
    };

    return await this.addItemsToCart([item]);
  }

  async addItemsToCart(items) {
    try {
      // Get sections to update
      let sectionsToBundle = [];
      
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        const sectionId = cartDrawer.closest('.shopify-section')?.id.replace('shopify-section-', '');
        if (sectionId) {
          sectionsToBundle.push(sectionId);
        }
      }
      
      // Add items to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ 
          items: items,
          sections: sectionsToBundle.join(',')
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add items to cart');
      }

      const responseData = await response.json();
      
      // Handle cart update
      await this.handleCartUpdate(responseData);
      
      // Show success (open cart drawer)
      this.showSuccessFeedback();
      
      return responseData;
      
    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.showErrorFeedback();
      throw error;
    }
  }

  async handleCartUpdate(responseData) {
    // Get updated cart data
    const cartResponse = await fetch('/cart.js');
    const cartData = await cartResponse.json();
    
    // Add sections from response if available
    if (responseData.sections) {
      cartData.sections = responseData.sections;
    }
    
    // Dispatch cart change event
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        baseEvent: 'variant:add',
        cart: cartData
      }
    }));
    
    // Dispatch variant add event (for cart drawer/notification)
    document.dispatchEvent(new CustomEvent('variant:add', {
      bubbles: true,
      detail: {
        items: responseData.hasOwnProperty('items') ? responseData.items : [responseData],
        cart: cartData
      }
    }));
  }

  showSuccessFeedback() {
    // Open cart drawer if exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      if (typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        // Try to trigger click on cart icon as fallback
        const cartIcon = document.querySelector('[aria-controls="cart-drawer"]');
        if (cartIcon) cartIcon.click();
      }
    }
  }

  showErrorFeedback() {
    console.error('Failed to add product to cart');
  }
}

// Make it globally accessible
window.AddOnsManager = AddOnsManager;

// Initialize when DOM is loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.addOnsManager = new AddOnsManager();
  });
} else {
  window.addOnsManager = new AddOnsManager();
}